import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

interface TypingUser {
  id: string;
  name: string;
}

export function useTypingIndicator(connectionId: string, userName: string) {
  const { user } = useAuth();
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!connectionId || !user?.id) return;

    const channel = supabase.channel(`typing-${connectionId}`, {
      config: {
        private: true,
        presence: {
          key: user.id,
        },
      },
    });

    channel
      .on("presence", { event: "sync" }, () => {
        const state = channel.presenceState();
        const typing: TypingUser[] = [];
        
        Object.entries(state).forEach(([userId, presences]) => {
          if (userId !== user.id && Array.isArray(presences)) {
            const presence = presences[0] as { isTyping?: boolean; name?: string };
            if (presence?.isTyping) {
              typing.push({
                id: userId,
                name: presence.name || "Someone",
              });
            }
          }
        });
        
        setTypingUsers(typing);
      })
      .subscribe(async (status) => {
        console.log(`[typing-${connectionId}] subscribe status:`, status);
        if (status === "SUBSCRIBED") {
          await channel.track({
            isTyping: false,
            name: userName,
          });
        }
      });

    channelRef.current = channel;

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      supabase.removeChannel(channel);
    };
  }, [connectionId, user?.id, userName]);

  const setTyping = useCallback(
    async (isTyping: boolean) => {
      if (!channelRef.current) return;

      await channelRef.current.track({
        isTyping,
        name: userName,
      });

      // Auto-clear typing after 3 seconds of inactivity
      if (isTyping) {
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
        typingTimeoutRef.current = setTimeout(async () => {
          if (channelRef.current) {
            await channelRef.current.track({
              isTyping: false,
              name: userName,
            });
          }
        }, 3000);
      }
    },
    [userName]
  );

  const stopTyping = useCallback(async () => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    if (channelRef.current) {
      await channelRef.current.track({
        isTyping: false,
        name: userName,
      });
    }
  }, [userName]);

  return {
    typingUsers,
    setTyping,
    stopTyping,
  };
}
